import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentEvents, GetTodoListCountResponse } from '@dreamai/types';
import { usePusher } from '../../../contexts/PusherContext';
import { useResponseArtifactEvents } from '../../../providers';
import { useAgent } from '../../../contexts/AgentContext';
import api from '../../../api';

const CALLBACK_ID = 'todo-list-count';

/**
 * Hook to fetch the "My Tasks" count for the To-Do List nav badge.
 * Counts: pending response artifacts (excl. information_dump) + pending user todos.
 * Refreshes on RESPONSE_ARTIFACT_CREATED/UPDATED (via ResponseArtifactEventProvider)
 * and USER_TODO_CREATED/UPDATED (via user Pusher channel).
 */
export const useTodoListCount = () => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;
  const { activeAgent } = useAgent();
  const activeAgentId = activeAgent?.id;
  const {
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  } = useResponseArtifactEvents();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchCount = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetTodoListCountResponse>(
        `/user-todos/count${activeAgentId ? `?agentId=${activeAgentId}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        setCount(response.data.count);
      }
    } catch (error) {
      console.error('Error fetching todo list count:', error);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, activeAgentId]);

  // Initial fetch
  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  // Listen for response artifact events via the shared provider
  useEffect(() => {
    registerCreatedCallback(CALLBACK_ID, () => fetchCount());
    registerUpdatedCallback(CALLBACK_ID, () => fetchCount());

    return () => {
      unregisterCreatedCallback(CALLBACK_ID);
      unregisterUpdatedCallback(CALLBACK_ID);
    };
  }, [
    fetchCount,
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  ]);

  // Listen for user todo events on the user channel
  useEffect(() => {
    if (!userChannel) return;

    const handleTodoChange = () => {
      fetchCount();
    };

    userChannel.bind(AgentEvents.USER_TODO_CREATED, handleTodoChange);
    userChannel.bind(AgentEvents.USER_TODO_UPDATED, handleTodoChange);

    return () => {
      userChannel.unbind(AgentEvents.USER_TODO_CREATED, handleTodoChange);
      userChannel.unbind(AgentEvents.USER_TODO_UPDATED, handleTodoChange);
    };
  }, [userChannel, fetchCount]);

  return { count, loading, refresh: fetchCount };
};
